/**
 * @file toc.tsx
 *
 * Table of Contents component for long-form documentation pages.
 *
 * Uses `IntersectionObserver` to track which section heading is currently in the
 * viewport and highlights the corresponding link. Smooth-scrolls to the target
 * when a link is clicked (respecting the browser's `prefers-reduced-motion`
 * media query via the native `scrollIntoView` API).
 *
 * @remarks
 * - SSR-safe: `IntersectionObserver` usage is guarded by a `typeof` check.
 * - The observer `rootMargin` is tuned for documentation layout with a fixed header
 *   (~100px) and early deactivation (~80% from bottom) so the active item changes
 *   before the section scrolls off-screen.
 */

import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { ScrollArea, type ScrollAreaProps } from "./scroll-area";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A single entry in the Table of Contents.
 *
 * @example
 * ```ts
 * const items: ToCItem[] = [
 *   { id: "installation", label: "Installation" },
 *   { id: "usage", label: "Usage" },
 * ];
 * ```
 */
export interface ToCItem {
	/** The DOM `id` attribute of the corresponding section heading. */
	id: string;
	/** Human-readable label shown in the ToC. */
	label: string;
	/**
	 * Heading level depth (1 for h2, 2 for h3, etc.).
	 * Indents lower-level items visually.
	 * @default 1
	 */
	depth?: number;
}

/**
 * Props for the `TableOfContents` component.
 */
export interface TableOfContentsProps {
	/**
	 * Ordered list of section items to display.
	 * Each item must have a matching DOM element with the same `id`.
	 */
	items: ToCItem[];
	/**
	 * Additional CSS classes applied to the root `<nav>` element.
	 * Use this to control positioning (e.g. sticky, fixed) from the consumer.
	 */
	className?: string;
	/**
	 * Configuration for the internal ScrollArea.
	 * @default { type: "hover" }
	 */
	scrollAreaProps?: Omit<ScrollAreaProps, "children">;
	/**
	 * Optional ref to the scroll container element (e.g. ScrollArea viewport).
	 * If provided, IntersectionObserver will use this container as its root.
	 */
	scrollContainerRef?: React.RefObject<HTMLElement | null | undefined>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Table of Contents sidebar component.
 *
 * Renders a `<nav>` sidebar with links to page sections. Tracks the active
 * section using `IntersectionObserver` and applies active styles to the
 * current link.
 *
 * @remarks
 * - Visible only on `xl` screens (`hidden xl:block` — sticky sidebar).
 * - The `aria-current` attribute is set on the active link for screen readers.
 * - Click scroll is smooth: `scrollIntoView({ behavior: "smooth" })`.
 *
 * @example
 * ```tsx
 * const toc: ToCItem[] = [
 *   { id: "overview", label: "Overview", depth: 1 },
 *   { id: "props", label: "Props", depth: 2 },
 *   { id: "examples", label: "Examples", depth: 1 },
 * ];
 *
 * <TableOfContents items={toc} />
 * ```
 *
 * @see https://m3.material.io/foundations/content-design/navigation
 */
export function TableOfContents({
	items,
	className,
	scrollAreaProps,
	scrollContainerRef,
}: TableOfContentsProps) {
	const [activeId, setActiveId] = useState("");

	// Stabilize dependency — re-subscribe only when the item IDs actually change.
	const itemIds = useMemo(() => items.map((i) => i.id), [items]);

	useEffect(() => {
		// SSR guard: IntersectionObserver is not available in Node.js.
		if (typeof IntersectionObserver === "undefined") return;

		const root = scrollContainerRef?.current ?? null;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) setActiveId(entry.target.id);
				}
			},
			// rootMargin: top offset ~100px (fixed header), bottom -80% (early switch)
			{ root, rootMargin: "-100px 0% -80% 0%" },
		);

		for (const id of itemIds) {
			const el = document.getElementById(id);
			if (el) observer.observe(el);
		}

		return () => observer.disconnect();
	}, [itemIds, scrollContainerRef]);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
			e.preventDefault();
			const target = document.getElementById(id);
			if (!target) return;

			target.scrollIntoView({ behavior: "smooth", block: "start" });
		},
		[],
	);

	return (
		<nav
			aria-label="On this page"
			className={cn("pl-6 flex flex-col h-full", className)}
		>
			<h4 className="text-xs font-bold text-m3-on-surface-variant uppercase tracking-widest mb-4 hidden lg:block">
				On this page
			</h4>
			<ScrollArea
				type="hover"
				{...scrollAreaProps}
				className={cn("flex-1 min-h-0", scrollAreaProps?.className)}
			>
				<ul className="space-y-2 pr-4">
					{items.map((item) => {
						const depth = item.depth ?? 1;
						const isSubItem = depth > 1;

						return (
							<li
								key={item.id}
								className={cn(
									isSubItem &&
										"pl-3.5 border-l border-m3-outline-variant/40 ml-1",
								)}
							>
								<a
									href={`#${item.id}`}
									onClick={(e) => handleClick(e, item.id)}
									aria-current={activeId === item.id ? "true" : undefined}
									className={cn(
										"transition-colors hover:text-m3-primary block truncate",
										isSubItem
											? "text-xs py-0.5 leading-relaxed"
											: "text-sm py-1 font-medium leading-snug",
										activeId === item.id
											? "text-m3-primary font-bold"
											: "text-m3-on-surface-variant",
									)}
									title={item.label}
								>
									{item.label}
								</a>
							</li>
						);
					})}
				</ul>
			</ScrollArea>
		</nav>
	);
}
